import React from "react";
import { Box, Typography, Grid, Chip, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

// Define the type for a single internal campaign in the merged campaign
interface InternalCampaignInMerge {
  timezone?: string;
  endDate?: string;
  startDate?: string;
  internalId?: string;
  platform: string;
  name: string;
  id: string;
}

// Define the type for the merged campaign info
interface MergedCampaignInfoType {
  id: string;
  name: string;
  description?: string | null;
  campaignId: string;
  campaignName: string;
  platform: string;
  startDate?: string;
  endDate?: string;
  timezone?: string;
  currency: string;
  internalCampaigns: InternalCampaignInMerge[];
}

interface MergedCampaignInfoProps {
  mergedCampaigns: MergedCampaignInfoType[] | null;
  isAdmin: boolean;
}

const MergedCampaignInfo: React.FC<MergedCampaignInfoProps> = ({
  mergedCampaigns,
  isAdmin,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!mergedCampaigns || mergedCampaigns.length === 0) {
    return null;
  }

  return (
    <>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        {t("campaigns.internalCampaign")}
      </Typography>

      {mergedCampaigns.map((mergeInfo, index) => (
        <Box
          key={mergeInfo.id}
          sx={{
            mb: 4,
            pb: 2,
            borderBottom:
              index < mergedCampaigns.length - 1
                ? `1px solid ${theme.palette.grey[200]}`
                : "none",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            {mergeInfo.name ||
              `${t("campaigns.internalCampaign")} ${index + 1}`}
          </Typography>

          {mergeInfo.description && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                {t("campaigns.description")}
              </Typography>
              <Typography variant="body1">{mergeInfo.description}</Typography>
            </Box>
          )}

          {isAdmin && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                {t("campaigns.internalCampaign")}
              </Typography>

              {mergeInfo.internalCampaigns &&
              mergeInfo.internalCampaigns.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {mergeInfo.internalCampaigns.map((campaign, idx) => (
                    <Chip
                      key={idx}
                      label={campaign.name || campaign.id}
                      size="small"
                      sx={{
                        bgcolor: theme.palette.primary.light,
                        color: theme.palette.primary.contrastText,
                        fontWeight: 500,
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {t("campaigns.noInternalCampaigns")}
                </Typography>
              )}
            </Box>
          )}

          <Grid container spacing={2}>
            {isAdmin && (
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t("campaigns.internalId")}
                  </Typography>
                  <Typography variant="body1">{mergeInfo.id}</Typography>
                </Box>
              </Grid>
            )}

            {isAdmin && (
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t("campaigns.campaignId")}
                  </Typography>
                  <Typography variant="body1">
                    {mergeInfo.campaignId}
                  </Typography>
                </Box>
              </Grid>
            )}

            {mergeInfo.platform && (
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t("campaigns.platform")}
                  </Typography>
                  <Typography variant="body1">{mergeInfo.platform}</Typography>
                </Box>
              </Grid>
            )}

            {mergeInfo.startDate && (
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t("campaigns.startDate")}
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(mergeInfo.startDate).format("DD MMM YYYY")}
                  </Typography>
                </Box>
              </Grid>
            )}

            {mergeInfo.endDate && (
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t("campaigns.endDate")}
                  </Typography>
                  <Typography variant="body1">
                    {dayjs(mergeInfo.endDate).format("DD MMM YYYY")}
                  </Typography>
                </Box>
              </Grid>
            )}

            {mergeInfo.timezone && (
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t("campaigns.timezone")}
                  </Typography>
                  <Typography variant="body1">{mergeInfo.timezone}</Typography>
                </Box>
              </Grid>
            )}

            {mergeInfo.currency && (
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    {t("campaigns.currency")}
                  </Typography>
                  <Typography variant="body1">{mergeInfo.currency}</Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      ))}
    </>
  );
};

export default MergedCampaignInfo;
